/**
 * Link Service
 * API calls for link operations
 */

import { Effect } from "effect";
import type {
  LinksListResponse,
  Link,
  LinkResponse,
  CreateLinkRequest,
  UpdateLinkRequest,
} from "@link-storage/contracts";
import { fromLinkResponse } from "@link-storage/contracts";
import type { AppError } from "@link-storage/common";
import { get, post, put, del } from "./apiClient.js";

/**
 * Get all links
 */
export const getLinks = (
  cursor?: string | null,
  limit = 20,
  tag?: string | null
): Effect.Effect<{ links: ReadonlyArray<Link>; nextCursor: string | null }, AppError> =>
  Effect.gen(function* (_) {
    const params: Record<string, string> = { limit: String(limit) };
    if (cursor) params["after"] = cursor;
    if (tag) params["tag"] = tag;
    const response = yield* _(get<LinksListResponse>("/links", params));
    return {
      links: response.links.map(fromLinkResponse),
      nextCursor: response.nextCursor ?? null,
    };
  });

/**
 * Get a single link by ID
 */
export const getLinkById = (id: string): Effect.Effect<Link, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(
      get<{ data: LinkResponse; error: null }>(`/links/${id}`)
    );
    return fromLinkResponse(response.data);
  });

/**
 * Create a new link
 */
export const createLink = (
  request: CreateLinkRequest
): Effect.Effect<Link, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(
      post<{ data: Link; error: null }>("/links", request)
    );
    return response.data;
  });

/**
 * Update a link's title
 */
export const updateLinkTitle = (
  id: string,
  request: UpdateLinkRequest
): Effect.Effect<Link, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(put<Link>(`/links/${id}`, request));
    return response;
  });

/**
 * Delete a link
 */
export const deleteLink = (id: string): Effect.Effect<void, AppError> =>
  Effect.gen(function* (_) {
    yield* _(del<void>(`/links/${id}`));
  });

/**
 * Refetch OG metadata for a link
 */
export const refetchOgMetadata = (id: string): Effect.Effect<Link, AppError> =>
  Effect.gen(function* (_) {
    const response = yield* _(
      post<{ data: Link; error: null }>(`/links/${id}/refetch-og`, {})
    );
    return response.data;
  });
